/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { WorkCreateInteractionActivityDto } from '../models/work-create-interaction-activity-dto';
import { WorkCreateInteractionDto } from '../models/work-create-interaction-dto';
import { WorkCreateInteractionNoteDto } from '../models/work-create-interaction-note-dto';
import { WorkCreateInteractionTaskDto } from '../models/work-create-interaction-task-dto';
import { WorkInteraction } from '../models/work-interaction';
import { WorkInteractionRelationTypeEnum } from '../models/work-interaction-relation-type-enum';
import { WorkUpdateInteractionDto } from '../models/work-update-interaction-dto';

@Injectable({ providedIn: 'root' })
export class WorkInteractionsV2Service extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `interactionsV2ControllerFindByRelationType()` */
  static readonly InteractionsV2ControllerFindByRelationTypePath = '/v2/interactions/byRelationType/{relationType}/{relationTypeId}';

  /**
   * Required permissions: interactions:read, interactions:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `interactionsV2ControllerFindByRelationType()` instead.
   *
   * This method doesn't expect any request body.
   */
  interactionsV2ControllerFindByRelationType$Response(
    params: {
      relationType: WorkInteractionRelationTypeEnum;
      relationTypeId: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<WorkInteraction>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkInteractionsV2Service.InteractionsV2ControllerFindByRelationTypePath, 'get');
    if (params) {
      rb.path('relationType', params.relationType, {});
      rb.path('relationTypeId', params.relationTypeId, {});
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<WorkInteraction>>;
      })
    );
  }

  /**
   * Required permissions: interactions:read, interactions:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `interactionsV2ControllerFindByRelationType$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  interactionsV2ControllerFindByRelationType(
    params: {
      relationType: WorkInteractionRelationTypeEnum;
      relationTypeId: number;
    },
    context?: HttpContext
  ): Observable<Array<WorkInteraction>> {
    return this.interactionsV2ControllerFindByRelationType$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<WorkInteraction>>): Array<WorkInteraction> => r.body)
    );
  }

  /** Path part for operation `interactionsV2ControllerCreate()` */
  static readonly InteractionsV2ControllerCreatePath = '/v2/interactions';

  /**
   * Required permissions: interactions:create, interactions:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `interactionsV2ControllerCreate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  interactionsV2ControllerCreate$Response(
    params: {
      body: (WorkCreateInteractionDto | WorkCreateInteractionActivityDto | WorkCreateInteractionNoteDto | WorkCreateInteractionTaskDto)
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkInteraction>> {
    const rb = new RequestBuilder(this.rootUrl, WorkInteractionsV2Service.InteractionsV2ControllerCreatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkInteraction>;
      })
    );
  }

  /**
   * Required permissions: interactions:create, interactions:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `interactionsV2ControllerCreate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  interactionsV2ControllerCreate(
    params: {
      body: (WorkCreateInteractionDto | WorkCreateInteractionActivityDto | WorkCreateInteractionNoteDto | WorkCreateInteractionTaskDto)
    },
    context?: HttpContext
  ): Observable<WorkInteraction> {
    return this.interactionsV2ControllerCreate$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkInteraction>): WorkInteraction => r.body)
    );
  }

  /** Path part for operation `interactionsV2ControllerDelete()` */
  static readonly InteractionsV2ControllerDeletePath = '/v2/interactions/{id}';

  /**
   * Required permissions: interactions:delete, interactions:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `interactionsV2ControllerDelete()` instead.
   *
   * This method doesn't expect any request body.
   */
  interactionsV2ControllerDelete$Response(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(this.rootUrl, WorkInteractionsV2Service.InteractionsV2ControllerDeletePath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(
      rb.build({ responseType: 'text', accept: '*/*', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Required permissions: interactions:delete, interactions:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `interactionsV2ControllerDelete$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  interactionsV2ControllerDelete(
    params: {
      id: number;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.interactionsV2ControllerDelete$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `interactionsV2ControllerUpdate()` */
  static readonly InteractionsV2ControllerUpdatePath = '/v2/interactions/{id}';

  /**
   * Required permissions: interactions:update, interactions:admin
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `interactionsV2ControllerUpdate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  interactionsV2ControllerUpdate$Response(
    params: {
      id: number;
      body: WorkUpdateInteractionDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkInteraction>> {
    const rb = new RequestBuilder(this.rootUrl, WorkInteractionsV2Service.InteractionsV2ControllerUpdatePath, 'patch');
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkInteraction>;
      })
    );
  }

  /**
   * Required permissions: interactions:update, interactions:admin
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `interactionsV2ControllerUpdate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  interactionsV2ControllerUpdate(
    params: {
      id: number;
      body: WorkUpdateInteractionDto
    },
    context?: HttpContext
  ): Observable<WorkInteraction> {
    return this.interactionsV2ControllerUpdate$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkInteraction>): WorkInteraction => r.body)
    );
  }

}
